import LZString from 'lz-string';
import { io } from 'socket.io-client';
import { createDemoSnapshot } from './demoData.js';
import { runtimeLogger } from './runtimeLogger.js';
import { releaseAssetUrl } from './runtimeAssets.js';
import { loadSocketWireRuntime } from './socketWireRuntime.js';
import { WIRE_CODEC } from './socketWire.js';
import { decodeRoad, applyRoadRecord } from '../domain/roadProtocol.js';
import { RoadWorkerClient } from './roadWorkerClient.js';
import { BrowserSocketProof } from './socketProof.js';
import { IdleRoundTracker, tableRoundKey } from '../domain/idleRounds.js';
import { deriveLobbyPhase, isLobbyRoadEmpty, LOBBY_PHASE } from '../domain/lobbyPhase.js';
import { ServerClock } from '../domain/serverClock.js';

export function decodeSocketPayload(value, fallback) {
  if (Array.isArray(value) || (value && typeof value === 'object')) return value;
  if (typeof value !== 'string' || !value) return fallback;
  try {
    const decoded = LZString.decompressFromUTF16(value);
    return decoded ? JSON.parse(decoded) : fallback;
  } catch (error) {
    runtimeLogger.error('[wanli-vue-data] decode socket payload failed', error);
    return fallback;
  }
}

const SESSION_STORAGE_KEY = 'wanli.vue.session.v1';

export function buildSocketAuthentication(loginTicket, clientType = 'desktop', reconnectTicket = '') {
  const clienttype = clientType === 'mobile' ? 'mobile' : 'desktop';
  if (loginTicket) return { loginTicket, clienttype };
  return { reconnectTicket, clienttype };
}

export function turnstileRuntimeScriptUrl(runtime = globalThis.window?.LoginTurnstile) {
  return runtime ? '' : releaseAssetUrl('/dist/desktop/TurnstileLogin.bundle.js');
}

export function buildPreloginPayload({ username, password, clientType = 'desktop', verification = {}, socketPublicKey, runtime = globalThis.window } = {}) {
  return {
    username: String(username || '').trim(),
    password,
    clientType: clientType === 'mobile' ? 'mobile' : 'desktop',
    releaseId: String(runtime?.__RELEASE_ID__ || '').trim(),
    ...(socketPublicKey ? { socketPublicKey } : {}),
    ...verification
  };
}

export function normalizeLobbySnapshot(snapshot = {}, { serverNowMs = null, roadRecords, roadReady } = {}) {
  const user = snapshot.user && typeof snapshot.user === 'object' ? snapshot.user : {};
  const rawTables = Array.isArray(snapshot.tables) ? snapshot.tables : [];
  const rawAnalysis = Array.isArray(snapshot.analysis) ? snapshot.analysis : [];
  const resultEventSeq = Array.isArray(snapshot.resultEventSeq) ? snapshot.resultEventSeq : [];
  const timeStartEventSeq = Array.isArray(snapshot.timeStartEventSeq) ? snapshot.timeStartEventSeq : [];
  const rawLimit = Array.isArray(user.UserLimt) && user.UserLimt[0] && typeof user.UserLimt[0] === 'object'
    ? user.UserLimt[0]
    : {};
  const tables = rawTables.slice(0, 4).map((table, index) => {
    const analysis = rawAnalysis[index] && typeof rawAnalysis[index] === 'object' ? rawAnalysis[index] : {};
    const pool = table?.BaccaratPool && typeof table.BaccaratPool === 'object' ? table.BaccaratPool : {};
    const countdown = Math.max(0, Number(table?.Countdown ?? table?.TableTimeSet ?? 0) || 0);
    const lastSetNum = table?.LastSetNum ?? table?.JuNum;
    const round = Number(lastSetNum) || 0;
    const roadRecord = roadReady?.has(index) ? roadRecords?.get(index) : undefined;
    const phase = deriveLobbyPhase({ countdown, lastSetNum, roadEmpty: isLobbyRoadEmpty(table, roadRecord), nowMs: serverNowMs });
    return {
      index,
      id: table?.ID ?? table?.TableID ?? index,
      sourceName: String(table?.Name || ''),
      displayName: `VIP ${index + 1}`,
      countdown,
      phase,
      bettingOpen: countdown > 0,
      lobbyBettingOpen: phase === LOBBY_PHASE.BETTING,
      banker: Number(analysis.Banker ?? 0) || 0,
      player: Number(analysis.Player ?? 0) || 0,
      tie: Number(analysis.Tie ?? 0) || 0,
      boot: Number(table?.LastBootNum ?? table?.BootNum ?? 0) || 0,
      round,
      lastSetId: table?.LastSetID ?? table?.SetsModel?.SetsID ?? table?.JuId ?? '',
      resultCode: String(table?.SetsModel?.Results ?? table?.Results ?? ''),
      resultEventSeq: Number(resultEventSeq[index]) || 0,
      timeStartEventSeq: Number(timeStartEventSeq[index]) || 0,
      pool: {
        banker: Number(pool.Banker ?? 0) || 0,
        player: Number(pool.Player ?? 0) || 0,
        tie: Number(pool.Tie ?? 0) || 0,
        bankerPair: Number(pool.BankerPair ?? 0) || 0,
        playerPair: Number(pool.PlayerPair ?? 0) || 0
      },
      analysis,
      raw: table
    };
  });
  return {
    user: {
      name: String(user.Name || user.UserName || user.Account || ''),
      balance: Number(user.AccountMoney ?? user.Balance ?? 0) || 0,
      limits: {
        mainMin: Number(rawLimit.MainMin ?? 0) || 0,
        mainMax: Number(rawLimit.MainMax ?? 0) || 0,
        tieMin: Number(rawLimit.TieMin ?? 0) || 0,
        tieMax: Number(rawLimit.TieMax ?? 0) || 0,
        pairMin: Number(rawLimit.PairMin ?? 0) || 0,
        pairMax: Number(rawLimit.PairMax ?? 0) || 0
      },
      raw: user
    },
    tables,
    news: String(rawTables[0]?.NewsList?.[0]?.NewsContent ?? '欢迎进入永利贵宾大厅，请选择桌台进入游戏，祝您游戏愉快。'),
    dateParts: Array.isArray(snapshot.dateParts) ? snapshot.dateParts : [],
    session: snapshot.session || {}
  };
}

export function extractAcceptedBetResult(result, fallbackBets = []) {
  let outcome = result?.OutMsg;
  if (typeof outcome === 'string') {
    try { outcome = JSON.parse(outcome); } catch (error) { outcome = null; }
  }
  const hasServerAmounts = outcome && typeof outcome === 'object'
    && Object.prototype.hasOwnProperty.call(outcome, 'BetValue');
  const source = Array.isArray(outcome?.BetValue)
    ? outcome.BetValue
    : String(outcome?.BetValue || '').split(',');
  if (hasServerAmounts && (source.length !== 5 || source.some(value =>
    String(value).trim() === '' || !Number.isFinite(Number(value)) || Number(value) < 0))) {
    const failure = new Error('下注金额返回异常，请先核对余额和下注记录，切勿重复下注');
    failure.outcomeUnknown = true;
    runtimeLogger.error('[wanli-bet] accepted response has invalid amount shape', { count: source.length });
    throw failure;
  }
  const accepted = Array.from({ length: 5 }, (_, index) => Math.max(0, Math.floor(Number(source[index]) || 0)));
  const fallback = Array.from({ length: 5 }, (_, index) => Math.max(0, Math.floor(Number(fallbackBets[index]) || 0)));
  return {
    // Only the legacy success response without BetValue uses requested amounts.
    // An explicit zero acknowledgement must not become a successful idle reset.
    bets: hasServerAmounts ? accepted : fallback,
    outcome: outcome && typeof outcome === 'object' ? outcome : {}
  };
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const absolute = new URL(src, window.location.href).href;
    const existing = Array.from(document.scripts).find((node) => node.src === absolute);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => reject(new Error(`脚本加载失败: ${src}`)), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`脚本加载失败: ${src}`));
    document.head.appendChild(script);
  });
}

export class DesktopDataService {
  constructor({
    mode = 'live',
    clientType = 'desktop',
    sessionStorageRef = globalThis.sessionStorage,
    monotonicNow,
    sessionStatusTimeoutMs = 6000
  } = {}) {
    if (!['live', 'demo'].includes(mode)) throw new Error(`Unsupported data mode: ${mode}`);
    if (!['desktop', 'mobile'].includes(clientType)) throw new Error(`Unsupported client type: ${clientType}`);
    this.mode = mode;
    this.clientType = clientType;
    this.socket = null;
    this.listeners = new Set();
    this.idleReturnListeners = new Set();
    this.idleRoundTracker = new IdleRoundTracker();
    this.videoWatchTableIndex = null;
    this.manualVideoEntry = null;
    this.videoWatchQueue = Promise.resolve();
    this.snapshot = normalizeLobbySnapshot({});
    this.resultEventSeq = [0, 0, 0, 0];
    this.resultEventKeys = ['', '', '', ''];
    this.timeStartEventSeq = [0, 0, 0, 0];
    this.raw = { user: {}, tables: [], analysis: [], dateParts: [], session: {}, resultEventSeq: this.resultEventSeq, timeStartEventSeq: this.timeStartEventSeq };
    this.countdownTimer = 0;
    this.tableTimeSyncTimer = 0;
    this.socketReady = false;
    this.serverClock = new ServerClock({ monotonicNow });
    this.turnstileHost = null;
    this.liveBettingEnabled = this.mode === 'live';
    this.pendingSocketRequests = new Map();
    this.localBetState = Array.from({ length: 4 }, () => ({ roundKey: '', bets: [0, 0, 0, 0, 0], history: [] }));
    this.sessionStorage = sessionStorageRef;
    this.sessionStorageKey = `${SESSION_STORAGE_KEY}:${this.clientType}`;
    this.loginTicket = '';
    this.reconnectTicket = '';
    this.sessionId = '';
    this.sessionStatusTimeoutMs = Math.max(1, Number(sessionStatusTimeoutMs) || 6000);
    this.backgroundBetOutcomeUnknown = false;
    this.backgroundSuspended = false;
    this.socketWireCodec = 'legacy';
    this.socketProof = new BrowserSocketProof();
    this.socketGeneration = 0;
    this.roadWorker = null;
    this.roadRecords = new Map();
    this.roadReady = new Set();
    this.roadResync = new Map();
    this.roadTablesReady = false;
    this.pendingRoadFrames = [];
    this.initialRoadReady = null;
    this.wireMetrics = { send: { packets: 0, plainBytes: 0, wireBytes: 0, compressed: 0 }, receive: { packets: 0, plainBytes: 0, wireBytes: 0, compressed: 0 }, roads: { packets: 0, results: 0, analysisMs: 0 } };
    this.restorePersistedSession();
  }

  restorePersistedSession() {
    if (this.mode !== 'live' || !this.sessionStorage) return;
    try {
      const saved = JSON.parse(this.sessionStorage.getItem(this.sessionStorageKey) || 'null');
      if (!saved || saved.clientType !== this.clientType) return;
      this.sessionId = String(saved.sessionId || '');
      this.reconnectTicket = String(saved.reconnectTicket || '');
    } catch (error) {
      runtimeLogger.warn('[wanli-vue-data] persisted session could not be read', error);
    }
  }

  persistSession() {
    if (this.mode !== 'live' || !this.sessionStorage || !this.sessionId || !this.reconnectTicket) return;
    try {
      this.sessionStorage.setItem(this.sessionStorageKey, JSON.stringify({
        sessionId: this.sessionId,
        reconnectTicket: this.reconnectTicket,
        clientType: this.clientType,
        updatedAt: Date.now()
      }));
    } catch (error) {
      runtimeLogger.warn('[wanli-vue-data] persisted session could not be written', error);
    }
  }

  clearPersistedSession() {
    try { this.sessionStorage?.removeItem(this.sessionStorageKey); } catch (error) {
      runtimeLogger.warn('[wanli-vue-data] persisted session could not be cleared', error);
    }
  }

  hasRestorableSession() {
    return this.mode === 'live' && Boolean(this.sessionId && this.reconnectTicket);
  }

  async verifySessionStatus() {
    if (this.mode === 'demo') return { valid: true, code: 'DEMO' };
    if (!this.sessionId) return { valid: false, code: 'SESSION_ID_MISSING', message: '登录会话已失效，请重新登录' };
    const startedAt = Date.now();
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), this.sessionStatusTimeoutMs);
    runtimeLogger.info('[wanli-lifecycle] session status check started', {
      clientType: this.clientType,
      timeoutMs: this.sessionStatusTimeoutMs
    });
    try {
      const response = await fetch('/auth/session-status', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: this.sessionId })
      });
      const payload = await response.json().catch(() => null);
      runtimeLogger.info('[wanli-lifecycle] session status check completed', {
        clientType: this.clientType,
        status: response.status,
        valid: Boolean(payload?.valid),
        durationMs: Date.now() - startedAt
      });
      if (!response.ok || !payload?.valid) {
        return {
          valid: false,
          code: payload?.code || `HTTP_${response.status}`,
          message: payload?.message || '登录会话已失效，请重新登录',
          transient: response.status >= 500 || !payload
        };
      }
      return payload;
    } catch (error) {
      const timedOut = controller.signal.aborted || error?.name === 'AbortError';
      const statusError = new Error(timedOut ? '登录状态检查超时，请稍后重试' : (error?.message || '登录状态检查失败'));
      statusError.code = timedOut ? 'SESSION_STATUS_TIMEOUT' : 'SESSION_STATUS_NETWORK_ERROR';
      statusError.transient = true;
      runtimeLogger.error('[wanli-lifecycle] session status check failed', {
        clientType: this.clientType,
        code: statusError.code,
        durationMs: Date.now() - startedAt
      });
      throw statusError;
    } finally {
      globalThis.clearTimeout(timeout);
    }
  }

  rejectPendingSocketRequests(reason = 'connection-interrupted') {
    this.pendingSocketRequests.forEach((pending, name) => {
      globalThis.clearTimeout(pending.timer);
      const isBet = name === 'TouZhuProc';
      if (isBet) this.backgroundBetOutcomeUnknown = true;
      const error = new Error(isBet
        ? '下注结果未确认，请先核对余额和下注记录，切勿重复下注'
        : `${name} 请求已中断，请稍后重试`);
      error.outcomeUnknown = isBet;
      error.reason = reason;
      pending.reject(error);
    });
    this.pendingSocketRequests.clear();
  }

  hasPendingSocketRequest(name) {
    return this.pendingSocketRequests.has(name);
  }

  suspendForBackground(reason = 'background', { disconnect = true } = {}) {
    if (this.mode === 'demo') return;
    this.backgroundSuspended = true;
    if (disconnect) this.stopTableTimeSync();
    else this.pauseTableTimeSync();
    this.rejectPendingSocketRequests(reason);
    if (disconnect) this.socket?.disconnect();
    runtimeLogger.info('[wanli-lifecycle] business socket background policy applied', {
      clientType: this.clientType,
      reason,
      disconnected: disconnect,
      socketConnected: Boolean(this.socket?.connected)
    });
  }

  resumeConnectedSession(reason = 'short-background') {
    if (this.mode === 'demo') return true;
    if (!this.socket?.connected || !this.socketReady) {
      runtimeLogger.info('[wanli-lifecycle] short resume requires full reconnect', {
        clientType: this.clientType,
        reason,
        socketConnected: Boolean(this.socket?.connected),
        socketReady: this.socketReady
      });
      return false;
    }
    this.backgroundSuspended = false;
    this.startTableTimeSync(this.socket);
    this.socket.emit('AccountMoney');
    runtimeLogger.info('[wanli-lifecycle] short resume kept authenticated business socket', {
      clientType: this.clientType,
      reason
    });
    return true;
  }

  consumeBackgroundBetOutcomeUnknown() {
    const value = this.backgroundBetOutcomeUnknown;
    this.backgroundBetOutcomeUnknown = false;
    return value;
  }

  reconnectSession() {
    if (this.mode === 'demo') return Promise.resolve(this.snapshot);
    if (!this.reconnectTicket || !this.sessionId) return Promise.reject(new Error('登录会话已失效，请重新登录'));
    this.backgroundSuspended = false;
    return this.connectSocket({ reconnectTicket: this.reconnectTicket, sessionId: this.sessionId });
  }

  getLocalBetState(tableIndex, roundKey = '') {
    const index = Math.max(0, Math.min(3, Number(tableIndex) || 0));
    const key = String(roundKey ?? '');
    const state = this.localBetState[index];
    if (key && state.roundKey && state.roundKey !== key) {
      state.bets = [0, 0, 0, 0, 0];
      state.history = [];
    }
    if (key) state.roundKey = key;
    return { roundKey: state.roundKey, bets: [...state.bets], history: [...state.history] };
  }

  ensureResultEventState() {
    if (!Array.isArray(this.resultEventSeq)) this.resultEventSeq = [0, 0, 0, 0];
    if (!Array.isArray(this.resultEventKeys)) this.resultEventKeys = ['', '', '', ''];
  }

  recordConfirmedBet(tableIndex, roundKey, bets, historyLines = []) {
    const current = this.getLocalBetState(tableIndex, roundKey);
    const index = Math.max(0, Math.min(3, Number(tableIndex) || 0));
    const state = this.localBetState[index];
    state.bets = current.bets.map((value, betIndex) => value + Math.max(0, Number(bets?.[betIndex]) || 0));
    state.history = [...historyLines.filter(Boolean), ...current.history];
    return { roundKey: state.roundKey, bets: [...state.bets], history: [...state.history] };
  }

  resetLocalBetState(tableIndex, roundKey = '') {
    const index = Math.max(0, Math.min(3, Number(tableIndex) || 0));
    this.localBetState[index] = { roundKey: String(roundKey ?? ''), bets: [0, 0, 0, 0, 0], history: [] };
    return this.getLocalBetState(index, roundKey);
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  subscribeIdleReturn(listener) {
    this.idleReturnListeners.add(listener);
    return () => this.idleReturnListeners.delete(listener);
  }

  setActiveTable(tableIndex) {
    if (tableIndex === null) {
      const previous = this.videoWatchTableIndex;
      this.videoWatchTableIndex = null;
      this.manualVideoEntry = null;
      this.idleRoundTracker.leave();
      if (previous !== null && this.mode === 'live' && this.socket?.connected && this.sessionId) {
        void this.enqueueVideoWatch('leave', previous, false);
      }
      return;
    }
    if (!Number.isInteger(tableIndex) || tableIndex < 0 || tableIndex > 3 || !this.raw.tables[tableIndex]) {
      throw new Error('无法启用闲置退桌：桌台数据不存在');
    }
    if (this.idleRoundTracker.tableIndex === tableIndex) return;
    this.videoWatchTableIndex = tableIndex;
    const roundKey = tableRoundKey(this.raw.tables[tableIndex]);
    const confirmed = this.localBetState[tableIndex];
    this.idleRoundTracker.enter(tableIndex, roundKey,
      confirmed?.roundKey === roundKey && confirmed.bets.some(value => value > 0));
    runtimeLogger.info('[wanli-idle] watching table', { clientType: this.clientType, tableIndex, roundKey, idleRounds: this.idleRoundTracker.idleRounds });
  }

  markManualVideoEntry(tableIndex) {
    this.manualVideoEntry = tableIndex;
  }

  enqueueVideoWatch(action, tableIndex, resume) {
    const sessionId = this.sessionId || this.raw.session?.sessionId || '';
    const result = this.videoWatchQueue.then(async () => {
      const startedAt = Date.now();
      const controller = new AbortController();
      const timeout = globalThis.setTimeout(() => controller.abort(), 6000);
      let response;
      let payload;
      try {
        response = await fetch('/auth/video-watch', {
          method: 'POST', credentials: 'include', signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, clientType: this.clientType, action, tableIndex, resume })
        });
        payload = await response.json();
      } finally { globalThis.clearTimeout(timeout); }
      if (!response.ok || !payload?.success) {
        if (payload?.code === 'VIDEO_IDLE_PAUSED') {
          this.idleRoundTracker.leave();
          this.idleReturnListeners.forEach(listener => listener({ tableIndex, idleRounds: 3, shouldReturn: true, source: 'server' }));
        }
        const error = new Error(payload?.message || '视频观看授权失败');
        error.code = payload?.code || 'VIDEO_WATCH_FAILED';
        throw error;
      }
      runtimeLogger.info('[wanli-idle] server watch updated', { clientType: this.clientType, action, tableIndex, resume, paused: !!payload.watch?.paused, idleRounds: payload.watch?.idleRounds, durationMs: Date.now() - startedAt });
      return payload;
    });
    // Keep ordered entry/leave operations usable after a failure, while callers
    // still receive the rejected operation and the failure is explicitly logged.
    this.videoWatchQueue = result.catch(error => {
      runtimeLogger.error('[wanli-idle] server watch operation failed', { clientType: this.clientType, action, tableIndex, code: error.code, message: error.message });
    });
    return result;
  }

  handleIdleRound(tableIndex, table) {
    const roundKey = tableRoundKey(table);
    if (!roundKey && this.idleRoundTracker.tableIndex === tableIndex) {
      runtimeLogger.warn('[wanli-idle] new round missing SetID', { clientType: this.clientType, tableIndex });
      return;
    }
    const event = this.idleRoundTracker.nextRound(tableIndex, roundKey);
    if (!event) return;
    runtimeLogger.info('[wanli-idle] completed round', { clientType: this.clientType, ...event });
    if (event.shouldReturn) this.idleReturnListeners.forEach(listener => listener(event));
  }

  acceptIdleRoundBet(tableIndex, roundKey, bets) {
    if (!bets.some(value => value > 0)) return;
    if (tableRoundKey(this.raw.tables[tableIndex]) !== roundKey) return;
    if (this.idleRoundTracker.acceptBet(tableIndex, roundKey)) {
      runtimeLogger.info('[wanli-idle] accepted bet resets idle count', { clientType: this.clientType, tableIndex, roundKey });
    }
  }

  emitSnapshot() {
    this.ensureResultEventState();
    this.raw.resultEventSeq = this.resultEventSeq;
    if (!Array.isArray(this.timeStartEventSeq)) this.timeStartEventSeq = [0, 0, 0, 0];
    this.raw.timeStartEventSeq = this.timeStartEventSeq;
    this.snapshot = normalizeLobbySnapshot(this.raw, {
      serverNowMs: this.mode === 'demo' ? Date.now() : this.serverClock.now(),
      roadRecords: this.roadRecords,
      roadReady: this.roadReady
    });
    this.listeners.forEach((listener) => listener(this.snapshot));
  }

  async prepareVerification(host) {
    this.turnstileHost = host || null;
    if (this.mode === 'demo' || !host) return;
    const runtimeScript = turnstileRuntimeScriptUrl();
    if (runtimeScript) await loadScriptOnce(runtimeScript);
    if (!window.LoginTurnstile) throw new Error('登录验证组件初始化失败');
    host.innerHTML = window.LoginTurnstile.createShellMarkup({ width: 320 });
    await window.LoginTurnstile.mount(host);
  }

  async getTurnstilePayload() {
    if (this.mode === 'demo' || !window.LoginTurnstile) return {};
    const result = typeof window.LoginTurnstile.getVerificationPayload === 'function'
      ? await window.LoginTurnstile.getVerificationPayload(this.turnstileHost)
      : { token: await window.LoginTurnstile.getToken(this.turnstileHost), proof: null };
    return {
      turnstileToken: result?.token || '',
      turnstileProof: result?.proof || null,
      turnstileClientDiagnostics: typeof window.LoginTurnstile.collectClientDiagnostics === 'function'
        ? await window.LoginTurnstile.collectClientDiagnostics(`wanli-vue-${this.clientType}-prelogin`)
        : null
    };
  }

  async login({ username, password }) {
    const safeUsername = String(username || '').trim();
    if (!safeUsername || !password) throw new Error('请输入账号和密码');
    if (this.mode === 'demo') {
      await wait(260);
      this.resultEventSeq = [0, 0, 0, 0];
      this.resultEventKeys = ['', '', '', ''];
      this.timeStartEventSeq = [0, 0, 0, 0];
      this.raw = createDemoSnapshot(safeUsername);
      this.emitSnapshot();
      this.startCountdowns();
      return this.snapshot;
    }

    const wire = await loadSocketWireRuntime();
    const socketPublicKey = wire.parser ? await this.socketProof.prepare() : null;
    let verification = {};
    try {
      verification = await this.getTurnstilePayload();
    } catch (error) {
      throw new Error(error?.message || '请先完成人机验证');
    }
    const response = await fetch('/auth/prelogin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPreloginPayload({
        username: safeUsername,
        password,
        clientType: this.clientType,
        socketPublicKey,
        verification
      }))
    });
    const payload = await response.json().catch(() => null);
    window.LoginTurnstile?.reset?.();
    if (!response.ok || !payload?.success) {
      const loginError = new Error(payload?.message || '登录失败，请重试');
      loginError.code = payload?.code || 'LOGIN_FAILED';
      loginError.refreshRequired = payload?.refreshRequired === true;
      loginError.requiredReleaseId = payload?.requiredReleaseId || '';
      throw loginError;
    }
    const oldSessionId = this.sessionId;
    if (socketPublicKey) await this.socketProof.store(payload.sessionId);
    if (oldSessionId && oldSessionId !== payload.sessionId) this.socketProof.remove(oldSessionId).catch(error => runtimeLogger.error('[socket-security] old proof key cleanup failed', { message: error.message }));
    this.clearPersistedSession();
    this.sessionId = payload.sessionId || '';
    this.loginTicket = payload.loginTicket || '';
    this.reconnectTicket = '';
    return this.connectSocket({ loginTicket: this.loginTicket, sessionId: this.sessionId });
  }

  async connectSocket({ loginTicket = '', reconnectTicket = '', sessionId = '' }) {
    if (!loginTicket && !reconnectTicket) return Promise.reject(new Error('登录票据缺失，请重新登录'));
    this.stopTableTimeSync();
    const generation = ++this.socketGeneration;
    const wire = await loadSocketWireRuntime({ onMetric: metric => {
      const counters = this.wireMetrics[metric.direction];
      counters.packets++;
      counters.plainBytes += metric.plainBytes;
      counters.wireBytes += metric.wireBytes;
      if (metric.kind & 1) counters.compressed++;
    } });
    if (generation !== this.socketGeneration) throw new Error('连接请求已被替换');
    this.socketWireCodec = wire.config.codec;
    if (wire.parser) await this.socketProof.restore(sessionId || this.sessionId);
    this.resetRoadAnalysis();
    if (wire.parser) this.roadWorker = new RoadWorkerClient();
    window.__WANLI_WIRE_METRICS__ = this.wireMetrics;
    this.loginTicket = loginTicket || '';
    this.reconnectTicket = reconnectTicket || this.reconnectTicket || '';
    this.sessionId = sessionId || this.sessionId || '';
    this.rejectPendingSocketRequests('socket-replaced');
    this.socket?.removeAllListeners?.();
    this.socket?.disconnect();
    const socket = io('/', {
      ...(wire.parser ? { parser: wire.parser } : {}),
      forceNew: true,
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionAttempts: 20
    });
    this.socket = socket;
    return new Promise((resolve, reject) => {
      let settled = false;
      const fail = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        this.stopTableTimeSync();
        socket.disconnect();
        this.resetRoadAnalysis();
        reject(error instanceof Error ? error : new Error(String(error || '连接失败')));
      };
      const timeout = window.setTimeout(() => fail(new Error('桌面客户端数据连接超时')), 15000);
      let loginReceived = false;
      let tablesReceived = false;
      const completeLogin = () => {
        if (!loginReceived || (wire.parser && (!tablesReceived || this.raw.tables.some((_table, index) => !this.roadReady.has(index))))) return;
        window.clearTimeout(timeout);
        this.socketReady = true;
        this.emitSnapshot();
        this.startCountdowns();
        this.startTableTimeSync(socket);
        socket.emit('AccountMoney');
        this.initialRoadReady = null;
        if (!settled) { settled = true; resolve(this.snapshot); }
      };
      if (wire.parser) {
        this.initialRoadReady = completeLogin;
        socket.on('TableSnapshot', tables => {
          if (!Array.isArray(tables) || tables.some(table => !table || typeof table !== 'object' || 'LastBootRoads' in table)) return fail(new Error('桌台快照协议异常'));
          this.raw.tables = tables;
          this.roadTablesReady = true;
          tablesReceived = true;
          const queued = this.pendingRoadFrames.splice(0);
          queued.forEach(payload => this.consumeBinaryRoad(payload, socket));
          completeLogin();
        });
      }

      socket.on('connect_error', (error) => {
        if (!settled) fail(new Error(error?.message || '无法连接桌面客户端数据服务'));
        else runtimeLogger.warn('[wanli-vue-data] socket reconnect error', { message: error?.message || String(error) });
      });
      let proofAuthentication = null;
      socket.on('challenge', async (challenge = {}) => {
        try {
          const authentication = buildSocketAuthentication(this.loginTicket, this.clientType, this.reconnectTicket);
          if (wire.parser) authentication.socketProof = await this.socketProof.sign(challenge, authentication, {
            releaseId: String(window.__RELEASE_ID__ || ''), hostname: window.location.hostname, socketId: socket.id
          });
          if (socket !== this.socket || !socket.connected) return;
          proofAuthentication = authentication;
          socket.emit('verifyChallenge', {
            nonce: challenge.nonce, signature: challenge.signature,
            host: window.location.hostname, origin: window.location.hostname
          });
        } catch (error) {
          if (!settled) fail(error);
          else {
            this.logout();
            window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: error.message, code: 'CLIENT_PROOF_FAILED' } }));
          }
        }
      });
      socket.on('challengeVerified', () => {
        if (!proofAuthentication) return fail(new Error('连接验证顺序异常'));
        socket.emit('authenticate', proofAuthentication);
        proofAuthentication = null;
      });
      socket.on('LoginValidationError', (payload = {}) => {
        const authError = new Error(payload.message || '登录验证失败');
        authError.code = payload.code || 'LOGIN_VALIDATION_FAILED';
        if (!settled) return fail(authError);
        this.logout();
        window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: authError.message, code: authError.code } }));
      });
      socket.on('LoginStatus', (success, userPayload, tablePayload, analysisPayload, dateParts, sessionMeta) => {
        if (!success) {
          const authError = new Error('登录会话已失效，请重新登录');
          if (!settled) return fail(authError);
          this.logout();
          window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: authError.message } }));
          return;
        }
        if (wire.parser) {
          this.resetRoadAnalysis({ keepWorker: true });
          this.initialRoadReady = completeLogin;
          tablesReceived = false;
        }
        this.loginTicket = '';
        try {
          this.serverClock.synchronize(sessionMeta?.serverNowMs);
        } catch (clockError) {
          console.error('[wanli-clock] server time rejected', { code: clockError.code });
          if (!settled) return fail(clockError);
          this.logout();
          window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: clockError.message, code: clockError.code } }));
          return;
        }
        this.sessionId = String(sessionMeta?.sessionId || this.sessionId || sessionId || '');
        this.reconnectTicket = String(sessionMeta?.reconnectTicket || '');
        this.persistSession();
        this.resultEventSeq = [0, 0, 0, 0];
        this.resultEventKeys = ['', '', '', ''];
        this.timeStartEventSeq = [0, 0, 0, 0];
        this.raw = {
          user: decodeSocketPayload(userPayload, {}),
          tables: decodeSocketPayload(tablePayload, []),
          analysis: decodeSocketPayload(analysisPayload, []),
          dateParts: Array.isArray(dateParts) ? dateParts : [],
          session: { sessionId: this.sessionId, ...(sessionMeta || {}), reconnectTicket: this.reconnectTicket },
          resultEventSeq: this.resultEventSeq,
          timeStartEventSeq: this.timeStartEventSeq
        };
        loginReceived = true;
        completeLogin();
      });
      this.bindRealtimeEvents(socket);
      socket.on('disconnect', (reason) => {
        this.stopTableTimeSync();
        this.rejectPendingSocketRequests(`socket-disconnect:${reason || 'unknown'}`);
        loginReceived = false;
        if (wire.parser) {
          this.resetRoadAnalysis({ keepWorker: true });
          tablesReceived = false;
          this.initialRoadReady = completeLogin;
        }
      });
      socket.connect();
    });
  }

  bindRealtimeEvents(socket) {
    socket.on('Message', message => {
      if (typeof message !== 'string') return;
      if (!this.raw.tables[0]) return;
      this.raw.tables[0].NewsList = [{ NewsContent: message }];
      this.emitSnapshot();
    });
    socket.on('VideoIdleReturn', watch => {
      if (!watch?.paused || watch.idleRounds < 3) return;
      this.idleRoundTracker.leave();
      runtimeLogger.info('[wanli-idle] server paused video', { clientType: this.clientType, tableIndex: watch.tableIndex, idleRounds: watch.idleRounds });
      this.idleReturnListeners.forEach(listener => listener({ ...watch, shouldReturn: true, source: 'server' }));
    });
    socket.on('Road', payload => {
      this.consumeBinaryRoad(payload, socket);
    });
    socket.on('GetTableTime', (values) => {
      if (!Array.isArray(values)) return;
      values.forEach((value, index) => {
        if (!this.raw.tables[index]) return;
        const localCountdown = Math.max(0, Number(this.raw.tables[index].Countdown) || 0);
        const serverCountdown = Math.max(0, Number(value) || 0);
        const driftSeconds = serverCountdown - localCountdown;
        if (Math.abs(driftSeconds) > 1) {
          runtimeLogger.warn('[wanli-countdown] server resync repaired drift', {
            tableIndex: index,
            localCountdown,
            serverCountdown,
            driftSeconds
          });
        }
        this.raw.tables[index].Countdown = serverCountdown;
      });
      this.emitSnapshot();
    });
    socket.on('Time', (value, index) => {
      if (this.raw.tables[index]) this.raw.tables[index].Countdown = Math.max(0, Number(value) || 0);
      this.timeStartEventSeq[index] = (this.timeStartEventSeq[index] || 0) + 1;
      this.emitSnapshot();
    });
    socket.on('StopTime', (_value, index) => {
      if (this.raw.tables[index]) this.raw.tables[index].Countdown = 0;
      this.emitSnapshot();
    });
    socket.on('LuDan', (table, index, analysis) => {
      // Phaser refreshes the authoritative balance on every result event so
      // settled winnings are reflected even when no further bet is placed.
      socket.emit('AccountMoney');
      this.ensureResultEventState();
      const decodedTable = decodeSocketPayload(table, table);
      if (this.raw.tables[index]) this.raw.tables[index] = decodedTable;
      if (this.socketWireCodec !== WIRE_CODEC) this.raw.analysis[index] = decodeSocketPayload(analysis, analysis || {});
      const resultCode = String(decodedTable?.SetsModel?.Results ?? decodedTable?.Results ?? '');
      const setId = String(decodedTable?.LastSetID ?? decodedTable?.SetsModel?.SetsID ?? decodedTable?.JuId ?? '');
      const eventKey = resultCode ? `${setId}:${resultCode}` : '';
      if (eventKey && eventKey !== this.resultEventKeys[index]) {
        this.resultEventKeys[index] = eventKey;
        this.resultEventSeq[index] = (this.resultEventSeq[index] || 0) + 1;
        runtimeLogger.info('[wanli-result] settlement event', { tableIndex: index, setId, resultCode, eventSeq: this.resultEventSeq[index] });
      }
      this.emitSnapshot();
    });
    socket.on('JuId', (table, index) => {
      if (this.raw.tables[index]) this.raw.tables[index] = decodeSocketPayload(table, table);
      runtimeLogger.info('[wanli-result] new round does not emit settlement', {
        tableIndex: index,
        setId: String(this.raw.tables[index]?.LastSetID ?? this.raw.tables[index]?.SetsModel?.SetsID ?? '')
      });
      this.handleIdleRound(index, this.raw.tables[index]);
      this.emitSnapshot();
    });
    socket.on('CaiChi', (payload, index) => {
      const table = this.raw.tables[index];
      if (!table || !Array.isArray(payload)) return;
      table.UserChipsList = payload[0] || table.UserChipsList;
      table.BaccaratPool = payload[1] || table.BaccaratPool;
      table.UsersCount = payload[2] ?? table.UsersCount;
      table.OnlineUser = payload[3] || table.OnlineUser;
      this.emitSnapshot();
    });
    socket.on('AccountMoney', (value) => {
      this.raw.user.AccountMoney = Number(value) || 0;
      this.emitSnapshot();
    });
    socket.on('TouZhuProc', (payload) => this.resolveSocketRequest('TouZhuProc', payload));
    socket.on('changePass', (result, message) => this.resolveSocketRequest('changePass', { result, message }));
    socket.on('GetBetReport', (payload) => this.resolveSocketRequest('GetBetReport', payload));
    socket.on('GetBetReportTotal', (payload) => this.resolveSocketRequest('GetBetReportTotal', payload));
    socket.on('ServerSideClose', () => {
      this.logout();
      window.dispatchEvent(new CustomEvent('wanli-session-ended', {
        detail: { message: '该账号已在其他设备登录，当前会话已下线' }
      }));
    });
  }

  resolveSocketRequest(name, payload) {
    const pending = this.pendingSocketRequests.get(name);
    if (!pending) return;
    window.clearTimeout(pending.timer);
    this.pendingSocketRequests.delete(name);
    pending.resolve(payload);
  }

  resetRoadAnalysis({ keepWorker = false } = {}) {
    for (const timer of this.roadResync.values()) clearTimeout(timer);
    this.roadResync.clear();
    this.roadRecords.clear();
    this.roadReady.clear();
    this.roadTablesReady = false;
    this.pendingRoadFrames = [];
    this.initialRoadReady = null;
    if (!keepWorker) { this.roadWorker?.close(); this.roadWorker = null; }
  }

  consumeBinaryRoad(payload, socket) {
    if (this.socketWireCodec === WIRE_CODEC && !this.roadTablesReady) {
      if (this.pendingRoadFrames.length < 64) { this.pendingRoadFrames.push(payload); return; }
      console.error('[road-client] initial road queue overflow');
      this.logout();
      window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: '路珠快照顺序异常，请重新连接', code: 'ROAD_QUEUE_OVERFLOW' } }));
      return;
    }
    this.handleBinaryRoad(payload, socket).catch(error => {
      if (socket !== this.socket) return;
      // A broken road must be visible, not silently display stale results.
      console.error('[road-client] analysis failed', { message: error?.message || String(error) });
      this.logout();
      window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: '路珠数据校验或计算失败，请重新连接', code: 'ROAD_ANALYSIS_FAILED' } }));
    });
  }

  async handleBinaryRoad(payload, socket) {
    if (this.socketWireCodec !== WIRE_CODEC) throw new Error('Unexpected binary road on legacy connection');
    const next = decodeRoad(payload);
    if (next.table >= this.raw.tables.length) throw new Error('Road table index outside snapshot');
    const applied = applyRoadRecord(this.roadRecords.get(next.table), next);
    if (applied.status === 'gap') {
      if (!this.roadResync.has(next.table)) {
        console.warn('[road-client] revision gap; requesting authoritative snapshot', { table: next.table, revision: next.revision, start: next.start });
        this.roadReady.delete(next.table);
        this.raw.analysis[next.table] = {};
        this.emitSnapshot();
        this.roadResync.set(next.table, setTimeout(() => {
          console.error('[road-client] snapshot resync timeout', { table: next.table });
          this.logout();
          window.dispatchEvent(new CustomEvent('wanli-session-ended', { detail: { message: '路珠同步超时，请重新连接', code: 'ROAD_RESYNC_TIMEOUT' } }));
        }, 10000));
        socket.emit('GetRoadSnapshot', next.table);
      }
      return;
    }
    if (applied.status !== 'applied') return;
    clearTimeout(this.roadResync.get(next.table));
    this.roadResync.delete(next.table);
    const record = applied.record;
    this.roadRecords.set(next.table, record);
    this.wireMetrics.roads.packets++;
    this.wireMetrics.roads.results += next.codes.length;
    // Reset/correction clears the old display immediately. Normal deltas keep
    // it visible until the worker returns the next revision.
    if (next.start === 0) { this.raw.analysis[next.table] = {}; this.roadReady.delete(next.table); this.emitSnapshot(); }
    const worker = this.roadWorker;
    let result;
    try { result = await worker.analyze(record); } catch (error) {
      if (socket !== this.socket || worker !== this.roadWorker || this.roadRecords.get(next.table) !== record) return;
      throw error;
    }
    if (socket !== this.socket || worker !== this.roadWorker || this.roadRecords.get(next.table) !== record) return;
    this.raw.analysis[next.table] = result.analysis;
    this.roadReady.add(next.table);
    this.wireMetrics.roads.analysisMs += result.durationMs || 0;
    runtimeLogger.info('[road-client] analyzed', { table: next.table, revision: record.revision, results: record.codes.length, durationMs: result.durationMs });
    this.emitSnapshot();
    this.initialRoadReady?.();
  }

  waitForSocketResponse(name, emit, timeoutMs = 12000) {
    if (!this.socket?.connected) return Promise.reject(new Error('实时数据连接已断开'));
    if (this.pendingSocketRequests.has(name)) return Promise.reject(new Error('上一笔请求仍在处理中'));
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pendingSocketRequests.delete(name);
        reject(new Error(`${name} 请求超时`));
      }, timeoutMs);
      this.pendingSocketRequests.set(name, { resolve, reject, timer });
      emit();
    });
  }

  async requestVideoGrant(tableIndex = this.videoWatchTableIndex) {
    if (this.mode === 'demo') return { enabled: false, grantToken: '', queryParamName: 'vg' };
    if (!Number.isInteger(tableIndex) || tableIndex < 0 || tableIndex > 3) throw new Error('视频观看桌台未选择');
    const resume = this.manualVideoEntry === tableIndex;
    this.manualVideoEntry = null;
    await this.enqueueVideoWatch('enter', tableIndex, resume);
    const response = await fetch('/auth/video-grant', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: this.sessionId || this.raw.session?.sessionId || '', clientType: this.clientType })
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload) throw new Error(payload?.message || '视频授权失败');
    return payload;
  }

  async placeBet({ tableIndex, bets, seat = 7 }) {
    const safeBets = Array.from({ length: 5 }, (_, index) => Math.max(0, Math.floor(Number(bets?.[index]) || 0)));
    if (!safeBets.some((value) => value > 0)) throw new Error('您还没有放置筹码');
    const table = this.raw.tables[tableIndex];
    if (!table) throw new Error('桌台数据不存在');
    const betRoundKey = tableRoundKey(table);
    if (this.mode === 'demo') {
      await wait(260);
      const total = safeBets.reduce((sum, value) => sum + value, 0);
      if (total > Number(this.raw.user.AccountMoney || 0)) throw new Error('账户余额不足');
      this.raw.user.AccountMoney = Number(this.raw.user.AccountMoney || 0) - total;
      const pool = table.BaccaratPool || (table.BaccaratPool = {});
      ['Banker', 'Player', 'Tie', 'BankerPair', 'PlayerPair'].forEach((key, index) => {
        pool[key] = Number(pool[key] || 0) + safeBets[index];
      });
      this.acceptIdleRoundBet(tableIndex, betRoundKey, safeBets);
      this.emitSnapshot();
      return { success: true, tableIndex, bets: safeBets, total, demo: true };
    }
    if (!this.liveBettingEnabled) {
      throw new Error('当前 live 测试环境已禁用真实下注');
    }
    const payload = [
      tableIndex,
      table.LastSetID ?? table.SetsModel?.SetsID ?? table.JuId ?? '',
      7,
      ...safeBets,
      1,
      seat
    ];
    let rawResult;
    try {
      rawResult = await this.waitForSocketResponse('TouZhuProc', () => this.socket.emit('TouZhuProc', payload));
    } finally {
      // The legacy endpoint can apply a bet and still fail while writing its HTTP response.
      // Always refresh the authoritative balance before the user can decide whether to retry.
      this.socket?.emit('AccountMoney');
    }
    let result = null;
    try {
      result = typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult;
    } catch (error) {
      const failure = new Error('下注结果未确认，请先核对余额和下注记录，切勿重复下注');
      failure.outcomeUnknown = true;
      throw failure;
    }
    if (!result?.Success) {
      const failure = new Error(result?.Message || '账户余额不足或当前不可下注');
      failure.outcomeUnknown = Boolean(result?.OutcomeUnknown);
      failure.requestId = result?.RequestId || '';
      throw failure;
    }
    const accepted = extractAcceptedBetResult(result, safeBets);
    this.acceptIdleRoundBet(tableIndex, betRoundKey, accepted.bets);
    const poolValues = String(accepted.outcome.CaiChi || '').split(',').map((value) => Number(String(value).replace(/,/g, '')) || 0);
    if (poolValues.length >= 5) {
      table.BaccaratPool = {
        ...(table.BaccaratPool || {}),
        Banker: poolValues[0],
        Player: poolValues[1],
        Tie: poolValues[2],
        BankerPair: poolValues[3],
        PlayerPair: poolValues[4]
      };
    }
    const rawAccountMoney = accepted.outcome.AccountMoney;
    const accountMoney = Number(String(rawAccountMoney ?? '').replace(/,/g, ''));
    if (rawAccountMoney !== undefined && rawAccountMoney !== null && rawAccountMoney !== '' && Number.isFinite(accountMoney)) {
      this.raw.user.AccountMoney = accountMoney;
    }
    this.emitSnapshot();
    return { success: true, tableIndex, bets: accepted.bets, total: accepted.bets.reduce((sum, value) => sum + value, 0), raw: result };
  }

  async changePassword(oldPassword, newPassword) {
    if (this.mode === 'demo') {
      await wait(180);
      return { result: true, message: '密码修改演示成功' };
    }
    return this.waitForSocketResponse('changePass', () => this.socket.emit('changePass', oldPassword, newPassword));
  }

  async getBetReport(startDate, endDate) {
    if (this.mode === 'demo') return JSON.stringify([
      { DesktopName: 'VIP1', SetTime: `${startDate.slice(0, 10)} 09:18:26`, BootsNum: 6, SetNum: 15, BetPosition: '庄胜', BetValue: 100, WinLose: 95, Results: '庄赢' },
      { DesktopName: 'VIP2', SetTime: `${startDate.slice(0, 10)} 10:42:09`, BootsNum: 6, SetNum: 21, BetPosition: '闲胜.闲对', BetValue: 200, WinLose: -200, Results: '庄赢.庄对' }
    ]);
    return this.waitForSocketResponse('GetBetReport', () => this.socket.emit('GetBetReport', startDate, endDate, 1, 10000), 45000);
  }

  async getBetReportTotal() {
    if (this.mode === 'demo') return JSON.stringify([
      { AccountPeriod: '2026/09/24', WinLose: -105, WashChip: 300, BetCounts: 2, isShowWashChip: true },
      { AccountPeriod: '2026/09/23', WinLose: 680, WashChip: 1200, BetCounts: 7, isShowWashChip: true }
    ]);
    return this.waitForSocketResponse('GetBetReportTotal', () => this.socket.emit('GetBetReportTotal'), 45000);
  }

  startCountdowns() {
    window.clearInterval(this.countdownTimer);
    this.countdownTimer = window.setInterval(() => {
      let changed = false;
      this.raw.tables.forEach((table, index) => {
        const current = Number(table.Countdown || 0);
        if (current > 0) {
          table.Countdown = current - 1;
          changed = true;
        } else if (this.mode === 'demo') {
          table.Countdown = 36 + index * 3;
          this.timeStartEventSeq[index] = (this.timeStartEventSeq[index] || 0) + 1;
          changed = true;
        }
      });
      if (changed) this.emitSnapshot();
    }, 1000);
  }

  startTableTimeSync(socket = this.socket) {
    window.clearInterval(this.tableTimeSyncTimer);
    this.tableTimeSyncTimer = 0;
    if (socket !== this.socket || !this.socketReady || !socket?.connected || this.mode !== 'live') return;
    this.requestTableTime();
    this.tableTimeSyncTimer = window.setInterval(() => {
      if (socket === this.socket) this.requestTableTime();
    }, 5000);
  }

  requestTableTime() {
    if (this.mode !== 'live' || !this.socketReady || !this.socket?.connected) return;
    this.socket.emit('GetTableTime');
  }

  pauseTableTimeSync() {
    window.clearInterval(this.tableTimeSyncTimer);
    this.tableTimeSyncTimer = 0;
  }

  stopTableTimeSync() {
    this.socketReady = false;
    this.pauseTableTimeSync();
  }

  logout({ clearSession = true } = {}) {
    this.idleRoundTracker.leave();
    if (clearSession && this.socketProof.sessionId) this.socketProof.remove(this.socketProof.sessionId).catch(error => runtimeLogger.error('[socket-security] proof key cleanup failed', { message: error.message }));
    this.socketGeneration++;
    this.resetRoadAnalysis();
    window.clearInterval(this.countdownTimer);
    this.countdownTimer = 0;
    this.stopTableTimeSync();
    this.socket?.disconnect();
    this.socket = null;
    this.rejectPendingSocketRequests('logout');
    this.loginTicket = '';
    this.reconnectTicket = '';
    this.sessionId = '';
    this.backgroundSuspended = false;
    this.backgroundBetOutcomeUnknown = false;
    this.serverClock.reset();
    if (clearSession) this.clearPersistedSession();
    this.localBetState = Array.from({ length: 4 }, () => ({ roundKey: '', bets: [0, 0, 0, 0, 0], history: [] }));
    this.resultEventSeq = [0, 0, 0, 0];
    this.resultEventKeys = ['', '', '', ''];
    this.timeStartEventSeq = [0, 0, 0, 0];
    this.raw = { user: {}, tables: [], analysis: [], dateParts: [], session: {}, resultEventSeq: this.resultEventSeq, timeStartEventSeq: this.timeStartEventSeq };
    this.emitSnapshot();
  }
}

export function createDesktopDataService(options) {
  return new DesktopDataService(options);
}
